import {
  buildPrompt,
  cardEffectiveContext,
  cellKey,
  parentContextLayers,
  relatedComponents,
  type CardDraft,
} from '@blueprint/domain'
import { useEffect, useMemo, useState } from 'react'
import { useBoardStore } from '../../state/board-store.js'
import { cardLocation } from '../../state/selectors.js'
import { useSessionStore } from '../../state/session-store.js'
import { useUiStore } from '../../state/ui-store.js'
import { PromptPreviewDialog } from './PromptPreviewDialog.js'
import { Dialog } from '../../components/Dialog.js'
import { Button } from '../../components/controls.js'
import { useMatchSet } from '../story-map/filter-context.js'
import styles from './PromptPreviewDialog.module.css'

/**
 * The board-card entry point: previews the card's committed values. Inclusion changes
 * made here are temporary to this preview session and are never written to the card.
 */
export function BoardCardPromptDialog() {
  const cardId = useUiStore((state) => state.promptCardId)
  const closePrompt = useUiStore((state) => state.closePrompt)

  /**
   * The last card previewed. Kept after closing so the dialog can render one commit
   * with `open={false}`, which is what returns focus to the card that opened it —
   * unmounting instead would skip that transition.
   */
  const [shown, setShown] = useState<string | null>(null)
  useEffect(() => {
    if (cardId) setShown(cardId)
  }, [cardId])

  const card = useBoardStore((state) => (shown ? state.entities?.cards[shown] : undefined))
  const entities = useBoardStore((state) => state.entities)

  const location = useMemo(
    () => (entities && card ? cardLocation(entities, card) : undefined),
    [entities, card],
  )

  const draft: CardDraft | null = useMemo(() => {
    if (!card) return null
    return {
      title: card.title,
      description: card.description,
      status: card.status,
      priority: card.priority,
      moscow: card.moscow,
      owner: card.owner,
      type: card.type,
      icon: card.icon,
      tags: card.tags,
      colour: card.colour,
      estimateMinutes: card.estimateMinutes,
      dueDate: card.dueDate,
      release: card.release,
      intent: card.intent,
      context: card.context,
      relationships: card.relationships,
    }
  }, [card])

  const contextParents = useMemo(
    () => (entities && card ? parentContextLayers(entities, { scope: 'card', rowId: card.rowId, columnId: card.columnId }) : []),
    [entities, card],
  )
  const related = useMemo(
    () => (entities && card ? relatedComponents(entities, card.id, card.relationships ?? []) : []),
    [entities, card],
  )

  if (!shown || !draft) return null

  return (
    <PromptPreviewDialog
      open={cardId !== null}
      onClose={closePrompt}
      draft={draft}
      location={location}
      cardId={shown}
      unsaved={false}
      contextParents={contextParents}
      related={related}
    />
  )
}

/** Combined preview of the filtered cards in a row or column. Selection is session-local. */
export function BoardScopePromptDialog() {
  const scope = useUiStore((state) => state.promptScope)
  const setScope = useUiStore((state) => state.setPromptScope)
  const entities = useBoardStore((state) => state.entities)
  const matches = useMatchSet()
  // During a Teamcentric Accounts trial no data leaves the tenant: no copy or download.
  const trial = useSessionStore((state) => state.trial)
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(new Set())
  const [notice, setNotice] = useState('')
  const [includeHeading, setIncludeHeading] = useState(true)
  const [includeDropdown, setIncludeDropdown] = useState(true)
  useEffect(() => {
    setExcluded(new Set())
    setNotice('')
    setIncludeHeading(true)
    setIncludeDropdown(true)
  }, [scope])

  const groups = useMemo(() => {
    if (!entities || !scope) return []
    const columnIds = scope.kind === 'column' ? [scope.id] : entities.columnIds
    return columnIds.flatMap((columnId) => {
      const column = entities.columns[columnId]
      if (!column) return []
      const lanes = entities.swimlaneIds.flatMap((laneId) => {
        const lane = entities.swimlanes[laneId]!
        const rows = (entities.rowIdsBySwimlane[laneId] ?? []).flatMap((rowId) => {
          if (scope.kind === 'row' && rowId !== scope.id) return []
          const cards = (entities.cardIdsByCell[cellKey(rowId, columnId)] ?? [])
            .filter((id) => matches === null || matches.has(id))
            .flatMap((id) => entities.cards[id] ? [entities.cards[id]!] : [])
          return cards.length ? [{ row: entities.rows[rowId]!, cards }] : []
        })
        return rows.length ? [{ lane, rows }] : []
      })
      return lanes.length ? [{ column, lanes }] : []
    })
  }, [entities, scope, matches])

  const cards = groups.flatMap((group) => group.lanes.flatMap((lane) => lane.rows.flatMap((row) => row.cards)))
  const selectedCount = cards.filter((card) => !excluded.has(card.id)).length
  const title = scope?.kind === 'row' ? entities?.rows[scope.id]?.title : scope ? entities?.columns[scope.id]?.title : undefined
  const markdown = useMemo(() => {
    if (!entities || !scope) return ''
    const parts = [`# ${scope.kind === 'row' ? 'Row' : 'Column'} prompt${includeHeading ? `: ${title ?? ''}` : ''}`]
    if (includeDropdown) {
      if (scope.kind === 'row') {
        const row = entities.rows[scope.id]
        parts.push(`Swimlane: ${row ? entities.swimlanes[row.swimlaneId]?.title ?? '' : ''}`)
      } else {
        const groupId = entities.columns[scope.id]?.groupId
        parts.push(`Column group: ${groupId ? entities.groups[groupId]?.title ?? '' : 'No group'}`)
      }
    }
    let hasPrompts = false
    for (const group of groups) {
      const laneParts: string[] = []
      for (const { lane, rows } of group.lanes) {
        const prompts = rows.flatMap(({ cards }) => cards
          .filter((card) => !excluded.has(card.id))
          .map((card) => {
            const location = cardLocation(entities, card)
            if (!includeHeading) {
              if (scope.kind === 'row') location.row = ''
              else location.column = ''
            }
            if (!includeDropdown) {
              if (scope.kind === 'row') location.swimlane = ''
              else location.columnGroup = null
            }
            // Keep each complete card prompt inside the surrounding group headings.
            return nestPrompt(
              buildPrompt({
                ...card,
                location,
                effectiveContext: cardEffectiveContext(entities, card.id),
                related: relatedComponents(entities, card.id, card.relationships ?? []),
              }).markdown,
            )
          }))
        if (prompts.length) {
          hasPrompts = true
          const heading = scope.kind === 'column' ? `### Swimlane: ${lane.title}\n\n` : ''
          laneParts.push(`${heading}${prompts.join('\n\n---\n\n')}`)
        }
      }
      if (laneParts.length) {
        const heading = scope.kind === 'row' ? `## Column: ${group.column.title}\n\n` : ''
        parts.push(`${heading}${laneParts.join('\n\n')}`)
      }
    }
    return hasPrompts ? parts.join('\n\n') : ''
  }, [entities, scope, title, groups, excluded, includeHeading, includeDropdown])

  const toggle = (ids: string[], checked: boolean) => {
    setExcluded((previous) => {
      const next = new Set(previous)
      for (const id of ids) {
        if (checked) next.delete(id)
        else next.add(id)
      }
      return next
    })
    setNotice('')
  }
  const checkbox = (label: string, ids: string[]) => {
    const count = ids.filter((id) => !excluded.has(id)).length
    return (
      <label className={styles.check}>
        <input type="checkbox" checked={count === ids.length && count > 0} disabled={!ids.length}
          ref={(input) => { if (input) input.indeterminate = count > 0 && count < ids.length }}
          onChange={(event) => toggle(ids, event.target.checked)} />
        <span>{label}</span>
      </label>
    )
  }
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('unavailable')
      await navigator.clipboard.writeText(markdown)
      setNotice('Copied to the clipboard.')
    } catch {
      setNotice('Clipboard access failed. Select the preview text or download the Markdown.')
    }
  }
  const download = () => {
    const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown;charset=utf-8' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${scope?.kind}-prompt-${scope?.id}.md`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={scope !== null && title !== undefined} xwide
      title={`${scope?.kind === 'row' ? 'Row' : 'Column'} prompt preview: ${title ?? ''}`}
      onClose={() => setScope(null)}
      footer={<>
        <span className={styles.footerNote}>{selectedCount} of {cards.length} cards selected · {markdown.length.toLocaleString()} characters</span>
        {trial ? null : <>
          <Button onClick={download} disabled={!selectedCount}>Download Markdown</Button>
          <Button variant="primary" onClick={() => void copy()} disabled={!selectedCount}>Copy</Button>
        </>}
        <Button onClick={() => setScope(null)}>Close</Button>
      </>}>
      {scope ? <>
        <p className={styles.banner}>Built from card details using saved prompt inclusion settings. Selection applies only to this preview{matches !== null ? ' and is limited to cards matching the current filters' : ''}.</p>
        {notice ? <p role="status">{notice}</p> : null}
        <div className={styles.scopeLayout}>
          <div className={styles.selection} aria-label="Cards to include">
            <fieldset className={styles.sideGroup}>
              <legend>Prompt context</legend>
              <label className={styles.check}><input type="checkbox" checked={includeHeading}
                onChange={(event) => { setIncludeHeading(event.target.checked); setNotice('') }} />
                Include {scope.kind} heading: {title}
              </label>
              <label className={styles.check}><input type="checkbox" checked={includeDropdown}
                onChange={(event) => { setIncludeDropdown(event.target.checked); setNotice('') }} />
                Include {scope.kind === 'row' ? 'swimlane' : 'column group'}: {scope.kind === 'row'
                  ? entities?.swimlanes[entities.rows[scope.id]?.swimlaneId ?? '']?.title
                  : entities?.groups[entities.columns[scope.id]?.groupId ?? '']?.title ?? 'No group'}
              </label>
            </fieldset>
            {checkbox('Select all cards', cards.map((card) => card.id))}
            {!cards.length ? <p>No cards match this scope and the current filters.</p> : null}
            {groups.map(({ column, lanes }) => <section key={column.id}>
              <h3>{column.title}</h3>
              {lanes.map(({ lane, rows }) => <div key={lane.id} className={styles.treeBranch}>
                {scope.kind === 'column'
                  ? checkbox(`Swimlane: ${lane.title}`, rows.flatMap(({ cards }) => cards.map((card) => card.id)))
                  : null}
                {rows.map(({ row, cards }) => <div key={row.id} className={styles.treeBranch}>
                  {scope.kind === 'column' ? checkbox(`Row: ${row.title}`, cards.map((card) => card.id)) : null}
                  {cards.map((card) => <div key={card.id}>{checkbox(card.title, [card.id])}</div>)}
                </div>)}
              </div>)}
            </section>)}
          </div>
          <pre className={styles.output} tabIndex={0} aria-label="Assembled scope prompt" data-testid="scope-prompt-output">
            {markdown || 'Select cards to preview their prompt.'}
          </pre>
        </div>
      </> : null}
    </Dialog>
  )
}

/** Shift Markdown headings without changing fenced code supplied in card details. */
function nestPrompt(markdown: string): string {
  let fence: string | null = null
  return markdown.split('\n').map((line) => {
    const marker = line.match(/^\s{0,3}(`{3,}|~{3,})/)
    if (marker) {
      if (fence === null) fence = marker[1]!
      else if (marker[1]![0] === fence[0] && marker[1]!.length >= fence.length) fence = null
      return line
    }
    return fence === null ? line.replace(/^(#{1,3}) /, '###$1 ') : line
  }).join('\n')
}
